import * as React from 'react';
import {
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';

import {useLoadingDelay} from '@packages/systems/spring-hooks';

import {
  useSelectionMode,
  useOptionListNavigation,
  isOptionGroup,
  CREATABLE_OPTION_VALUE,
} from '../../hooks';

import {AddIcon} from '../../icons';

import type {
  FilterOptionsType,
  OptionsType,
  OptionType,
  SearchableListProps,
  SearchableListWrapperProps,
} from './_types';

import {
  SearchableListIndexContext,
  SearchableListContext,
  SearchableListSelectionContext,
} from './useSearchableList';

/**
 * This function constructs a string from the option's label and tags to be searched against.
 * This searchable text is used to compare against the input value.
 *
 * @returns boolean - whether the input value matches the searchable text
 */
const compareSearchableText = (inputValue: string, option: OptionType) => {
  const tagString = option.meta?.tags || [];
  const searchString = [...tagString, option.label].join(' ').toLowerCase();

  return searchString.includes(inputValue);
};

const defaultFilter: FilterOptionsType = ({targetValue, options}) => {
  const trimmedTargetValue = targetValue.trim().toLowerCase();

  if (trimmedTargetValue === '') {
    return options;
  }

  return options
    .map((option) => {
      // Filter through the grouped options
      if (isOptionGroup(option)) {
        const filteredGroupOptions = option.options.filter((groupOption) =>
          compareSearchableText(trimmedTargetValue, groupOption)
        );

        if (filteredGroupOptions.length) {
          return {...option, options: filteredGroupOptions};
        }
        // Filter the flat options
      } else if (compareSearchableText(trimmedTargetValue, option)) {
        return option;
      }

      return null;
    })
    .filter((option) => option !== null);
};

const defaultRenderCreateLabel = (inputValue: string) => {
  return `Create ${inputValue}`;
};

/**
 * # SearchableList
 *
 * Short, descriptive summary here.
 *
 * ## Usage
 *
 * ```jsx
 * import {SearchableList} from '@packages/systems/spring';
 * ```
 *
 * @component
 */
export const SearchableListWrapper = <TOptions extends OptionsType>({
  checkmarkStyle = 'default',
  filterDebounceDelay = 150,
  filterOptions: filterOptionsProp,
  floatingContext,
  floatingRefs,
  floatingStyles,
  inputValue: controlledInputValue,
  _isCreatable: isCreatable = false,
  isDisabled = false,
  isLoading: isLoadingProp = false,
  _isOpen: isOpen = true,
  maxSelections,
  onChange,
  _onCreateOption: onCreateOption,
  onInputChange,
  options,
  _renderCreateIcon: renderCreateIconProp,
  _renderCreateLabel: renderCreateLabelProp,
  renderEmptyStateLabel,
  renderOption,
  selectionMode = 'single',
  _shouldCloseOnScroll: shouldCloseOnScroll,
  value = '',
  children,
  scrollContainerRef,
  shouldHideSelectedOptions = false,
  shouldNotBubbleDismiss = false,
  shouldFilterOverrideOptionsProp,
  getSelectedOptionByValue,
}: SearchableListWrapperProps<TOptions>) => {
  const [uncontrolledInputValue, setUncontrolledInputValue] =
    React.useState<string>('');

  const renderCreateIcon = React.useMemo(
    () => (renderCreateIconProp === undefined ? AddIcon : renderCreateIconProp),
    [renderCreateIconProp]
  );

  const renderCreateLabel = React.useMemo(
    () => renderCreateLabelProp || defaultRenderCreateLabel,
    [renderCreateLabelProp]
  );

  const inputRef = React.useRef<HTMLInputElement>(null);

  const inputValue = React.useMemo(
    () => controlledInputValue ?? uncontrolledInputValue,
    [controlledInputValue, uncontrolledInputValue]
  );

  const filterOptions = React.useCallback(
    async ({
      targetValue,
      options: _options,
    }: {
      targetValue: string;
      options: OptionsType;
    }) => {
      /**
       * If a custom filter has been provided, we'll use it to filter the options.
       * Otherwise, we'll apply our default filter.
       */
      const filterFunction = filterOptionsProp || defaultFilter;
      const filteredOptions = await filterFunction({
        targetValue,
        options: _options,
      });

      /**
       * We show a "Create" option if the following criteria is met:
       * - isCreatable has been set to true.
       * - The targetValue does not match an existing option label or value.
       * - The targetValue does not match an existing selected value.
       */
      let shouldRenderCreatableOption;

      /**
       * Even though we do not trim for custom filters, we trim the targetValue here to ensure that the "Create" option is not rendered for whitespace.
       *
       * If this becomes a concern or issue, we can readjust.
       */
      const trimmedTargetValue = targetValue.trim();
      if (isCreatable && trimmedTargetValue) {
        const isTargetValueInOptionsArray = _options.some(
          (option) =>
            !isOptionGroup(option) &&
            (option.label === trimmedTargetValue ||
              option.value === trimmedTargetValue)
        );

        const isTargetValueSelected =
          typeof value === 'string'
            ? value === trimmedTargetValue
            : value.some(
                (selectedValue) => selectedValue === trimmedTargetValue
              );

        shouldRenderCreatableOption =
          !isTargetValueInOptionsArray && !isTargetValueSelected;
      }

      if (shouldRenderCreatableOption) {
        const creatableOption = {
          value: CREATABLE_OPTION_VALUE,
          label: renderCreateLabel(trimmedTargetValue),
          prefix: renderCreateIcon,
        };

        return filteredOptions.concat(creatableOption);
      }

      return filteredOptions;
    },
    [filterOptionsProp, isCreatable, renderCreateLabel, renderCreateIcon, value]
  );

  const {
    activeIndex,
    disabledIndices,
    flattenedOptions,
    setActiveIndex,
    listRef,
    getIndexByValue,
    isResyncing,
    visibleOptions,
  } = useOptionListNavigation({
    isCreatable,
    isDisabled: isDisabled || isLoadingProp,
    inputValue,
    options,
    loop: true,
    scrollContainerRef: scrollContainerRef ?? floatingRefs.floating,
    shouldHideSelectedOptions,
    selectedValues: value,
    filterOptions,
    filterDebounceDelay,
    shouldFilterOverrideOptionsProp,
  });

  const {
    toggleSelectedValue,
    getIsSelected,
    selectedOptions,
    hasMaxSelections,
  } = useSelectionMode({
    flattenedOptions,
    maxSelections,
    // @ts-expect-error - For some reason, typescript can't pass discriminated union types through to discriminated union types. ¯\_(ツ)_/¯
    onChange,
    getIndexByValue,
    getSelectedOptionByValue,
    selectionMode,
    value,
  });

  const role = useRole(floatingContext, {role: 'listbox'});
  const dismiss = useDismiss(floatingContext, {
    ancestorScroll: shouldCloseOnScroll,
    bubbles: shouldNotBubbleDismiss ? false : !inputValue,
  });

  const listNav = useListNavigation(floatingContext, {
    listRef,
    activeIndex,
    disabledIndices,
    /**
     * Generally, we want to disable keyboard navigation when the list is loading.
     * However, we need to allow the dropdown to open when the list is loading, and doing
     * so is controlled by useListNavigation.
     *
     * So, we only want to disable keyboard navigation when the list is loading AND the list is open.
     */
    enabled: !(isLoadingProp && isOpen),
    loop: true,
    onNavigate: setActiveIndex,
    virtual: true,
  });

  const {getItemProps, getFloatingProps, getReferenceProps} = useInteractions([
    role,
    dismiss,
    listNav,
  ]);

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isDisabled) {
        const targetValue = event?.target.value;

        if (
          controlledInputValue === undefined ||
          controlledInputValue === null
        ) {
          /**
           * Set internal input value if SearchableList is not controlled
           */
          setUncontrolledInputValue(targetValue);
        }

        onInputChange && onInputChange(event);
      }
    },
    [controlledInputValue, isDisabled, onInputChange]
  );

  /**
   * We need to track our initial loading and options state to determine if we should show a loading spinner.
   *
   * If the initial options array is empty and isLoadingProp is true, we should show a loading spinner.
   * Otherwise, we should use the useLoadingDelay hook to determine if we should show a loading spinner, displaying the information they provided.
   */
  const hasInitialStateChangedRef = React.useRef(false);
  const initialLoadingOrOptionsEmptyRef = React.useRef(
    options.length === 0 && isLoadingProp
  );

  /**
   * When either the options array or isLoadingProp changes from its initial state, we should set the hasInitialStateChangedRef to true.
   */
  React.useEffect(() => {
    if (!hasInitialStateChangedRef.current) {
      const optionsChanged = options.length > 0 || !isLoadingProp;
      if (optionsChanged) {
        hasInitialStateChangedRef.current = true;
      }
    }
  }, [options, isLoadingProp]);

  /**
   * Show loading spinner immediately if options are empty and isLoadingProp is true on initial render.
   * Otherwise, use useLoadingDelay as source of truth.
   */
  const isLoadingFromDelay = useLoadingDelay(isLoadingProp || isResyncing);
  const isLoading =
    !hasInitialStateChangedRef.current &&
    initialLoadingOrOptionsEmptyRef.current
      ? true
      : isLoadingFromDelay;

  const contextValue = React.useMemo(
    () => ({
      context: floatingContext,
      floatingStyles,
      getFloatingProps,
      getReferenceProps,
      handleInputChange,
      inputValue,
      isLoading,
      listRef,
      isOpen,
      options: visibleOptions,
      refs: floatingRefs,
      renderEmptyStateLabel,
      renderOption,
    }),
    [
      floatingContext,
      floatingStyles,
      getFloatingProps,
      getReferenceProps,
      handleInputChange,
      inputValue,
      isLoading,
      isOpen,
      listRef,
      floatingRefs,
      renderEmptyStateLabel,
      renderOption,
      visibleOptions,
    ]
  );

  const indexContextValue = React.useMemo(
    () => ({
      activeIndex,
      setActiveIndex,
      getItemProps,
      listRef,
      getIndexByValue,
      flattenedOptions,
      disabledIndices,
    }),
    [
      activeIndex,
      setActiveIndex,
      getItemProps,
      listRef,
      getIndexByValue,
      flattenedOptions,
      disabledIndices,
    ]
  );

  const selectionContextValue = React.useMemo(
    () => ({
      hasMaxSelections,
      inputRef,
      selectionMode,
      toggleSelectedValue,
      getIsSelected,
      selectedOptions,
      checkmarkStyle,
      isDisabled,
      onCreateOption,
    }),
    [
      hasMaxSelections,
      inputRef,
      selectionMode,
      toggleSelectedValue,
      getIsSelected,
      selectedOptions,
      checkmarkStyle,
      isDisabled,
      onCreateOption,
    ]
  );

  return (
    // @ts-expect-error - TS2322 - Type 'TOptions' is not assignable to type 'OptionsType'.
    <SearchableListContext.Provider value={contextValue}>
      <SearchableListIndexContext.Provider value={indexContextValue}>
        <SearchableListSelectionContext.Provider value={selectionContextValue}>
          {children}
        </SearchableListSelectionContext.Provider>
      </SearchableListIndexContext.Provider>
    </SearchableListContext.Provider>
  );
};

export const SearchableList = <TOptions extends OptionsType>({
  checkmarkStyle = 'default',
  filterDebounceDelay = 150,
  filterOptions: filterOptionsProp,
  inputValue: controlledInputValue,
  isDisabled = false,
  isLoading = false,
  maxSelections,
  onChange,
  onInputChange,
  options,
  renderEmptyStateLabel,
  renderOption,
  selectionMode = 'single',
  value = '',
  children,
  shouldHideSelectedOptions = false,
  shouldFilterOverrideOptionsProp,
  getSelectedOptionByValue,
}: SearchableListProps<TOptions>) => {
  const {refs, context, floatingStyles} = useFloating({open: true});

  return (
    // @ts-expect-error - TS2322 - Type narrowing for SearchableListWrapperProps<TOptions>
    <SearchableListWrapper
      checkmarkStyle={checkmarkStyle}
      filterDebounceDelay={filterDebounceDelay}
      filterOptions={filterOptionsProp}
      floatingContext={context}
      floatingRefs={refs}
      floatingStyles={floatingStyles}
      getSelectedOptionByValue={getSelectedOptionByValue}
      inputValue={controlledInputValue}
      isDisabled={isDisabled}
      isLoading={isLoading}
      maxSelections={maxSelections}
      onChange={onChange}
      onInputChange={onInputChange}
      options={options}
      renderEmptyStateLabel={renderEmptyStateLabel}
      renderOption={renderOption}
      selectionMode={selectionMode}
      shouldFilterOverrideOptionsProp={shouldFilterOverrideOptionsProp}
      shouldHideSelectedOptions={shouldHideSelectedOptions}
      value={value}
    >
      {children}
    </SearchableListWrapper>
  );
};

SearchableList.displayName = 'SearchableList';

export default SearchableList;
